/**
 * 業務フロー図の節点・札の見た目と、分岐の枝の色。
 *
 * 形で種別を描き分ける。業務は矩形、開始・終了は錠剤形、並列の分岐・合流は「＋」の菱形、出口へ・
 * 行き先の無い止まるは地を沈めた小さな札。2026-09-21 の図（`d2a1aab38` の `user-story-flow-nodes.tsx`）の
 * 描き分けに揃えてある。形が揃っていないと「業務」と「制御点」の違いが図から消える。
 *
 * **線の出入りする高さ（`NODE_HEIGHT / 2`）を種別によらず揃える。** 端の節点・菱形・札は升目と同じ
 * 高さの枠（`ControlSlot`）の縦中央に置く。種別ごとに高さが違うと、線が図形の中心ではない高さに
 * 出入りする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の節点（業務・開始・終了・並列の分岐と合流）と札（出口・止まる）を種別ごとの形で描き、分岐の枝の色を決める。
 * @graph-connects none
 */
import type { FlowNodeView } from '@cortex/domain-spec-console';
import type { ReactNode } from 'react';

import type { FlowChangeState } from '../flow-workspace/flow-diff-marks.js';
import {
  flowNodeBorderClass,
  flowNodeFillStyle,
  FlowNodeChangeMark,
} from '../flow-workspace/flow-node-change.js';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  PARALLEL_SHAPE_WIDTH,
  TERMINAL_SHAPE_HEIGHT,
  TERMINAL_SHAPE_WIDTH,
} from './constants.js';
import { estimateLineCount } from './edges.js';
import type { DiagramShape } from './layout.js';

/**
 * 分岐から出る枝の色。
 *
 * 1 つの節点から 2 本以上が出るとき、線と結末のラベルを同じ色で塗る。ラベルは線から離れた帯に
 * 置くので、位置だけではどのラベルがどの枝の説明なのかを追えない。色を共有させれば、線とラベルの
 * 対応が一目で付く。
 *
 * 明暗どちらのテーマでも読める彩度・明度に揃えた固定値にする。テーマトークンを使わないのは、
 * トークンが「成功 / 警告 / 危険」といった**意味**を持っており、枝の区別に流用すると色の意味が
 * 二重になるためである。
 * @graph-connects none
 */
export const BRANCH_COLORS = [
  '#5a9bff',
  '#e8763d',
  '#35b07a',
  '#b878e0',
  '#d9a520',
  '#3fb6cc',
] as const;

/**
 * 枝 1 本の色。分岐していない線は色を持たない。
 *
 * 1 本しか出ていない線に色を付けると、図の中の色数が増えるだけで何も区別しない。色は「同じ節点
 * から出た枝どうしの区別」にだけ使う。
 *
 * @param order 始点から出る線のうち何本目か（0 始まり）
 * @param outgoingTotal 始点から出る線の総数
 * @returns 色。分岐していなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分岐から出る枝の色を、何本目かから決める。
 * @graph-connects none
 */
export function branchColorOf(order: number, outgoingTotal: number): string | null {
  return outgoingTotal > 1 ? BRANCH_COLORS[order % BRANCH_COLORS.length] : null;
}

/** 節点の中身に出す語。 */
export interface FlowNodeLabels {
  /** 開始の節点の見出し */
  start: string;
  /** 終了の節点の見出し */
  end: string;
  /** 並列の分岐の節点の見出し */
  parallelSplit: string;
  /** 並列の合流の節点の見出し */
  parallelJoin: string;
  /** 業務を引けない節点の見出し */
  businessMissing: string;
}

/**
 * 節点 1 個の表示名。
 *
 * **種別で分ける。** 業務以外の節点は契約上 `business` が null と決まっているので、業務のラベル
 * だけを見ると必ず「業務が未記入」になる。図と詳細ペインの両方がこの 1 本を通る。
 *
 * @param node 節点
 * @param labels 節点の中身に出す語
 * @returns 表示名
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点 1 個の表示名を、種別（業務・開始・終了・並列の分岐・並列の合流）で分けて決める。
 * @graph-connects none
 */
export function flowNodeLabelOf(node: FlowNodeView, labels: FlowNodeLabels): string {
  if (node.kind === 'business') {
    return node.business === null || node.business.label === ''
      ? labels.businessMissing
      : node.business.label;
  }
  return labels[node.kind];
}

/**
 * 図形の横幅。線の出口を図形の右端に合わせるために使う。
 *
 * 升目の幅を全種別に使わないのは、菱形と錠剤形が升目より狭く、升目の右端から線を出すと図形から
 * 離れた空中から線が生えて見えるためである。
 *
 * @param shape 形
 * @returns 図形の横幅
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の節点・札の図形の横幅を形から決める。
 * @graph-connects none
 */
export function shapeWidthOf(shape: DiagramShape): number {
  if (shape === 'business') return NODE_WIDTH;
  if (shape === 'parallelSplit' || shape === 'parallelJoin') return PARALLEL_SHAPE_WIDTH;
  return TERMINAL_SHAPE_WIDTH;
}

/**
 * 図形の左端が升目の左端から下がる量。図形は升目の中央に置く。
 *
 * 左端へ寄せると、出口側だけに升目の余りが集まり、図形から次の節点までが不自然に長い線になる。
 *
 * @param shape 形
 * @param cellWidth 升目の幅
 * @returns 左の余り
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の図形を升目の中央に置くための左の余りを決める。
 * @graph-connects shapeWidthOf [calls] 図形の横幅
 */
export function shapeInsetOf(shape: DiagramShape, cellWidth: number): number {
  return Math.max(0, (cellWidth - shapeWidthOf(shape)) / 2);
}

/** 業務の節点の見出しが使える幅（px）。左右の内側余白を引いた残り。 @graph-connects none */
const NODE_TEXT_WIDTH = NODE_WIDTH - 24;

/** 業務の節点の見出しの文字の大きさ（px）。`text-sm` と揃える。 @graph-connects none */
const NODE_FONT_SIZE = 14;

/** 業務の節点の見出しの行送り（px）。`leading-5` と揃える。 @graph-connects none */
const NODE_LINE_HEIGHT = 20;

/** 業務の節点の見出し以外が使う高さ（px）。差分の帯と上下の内側余白。 @graph-connects none */
const NODE_CHROME_HEIGHT = 44;

/**
 * 節点・札が縦に占める高さ。ラベルが避ける面の高さに使う。
 *
 * 業務の節点は名前を折り返して全文を出すので、長い名前では下限（`NODE_HEIGHT`）より高くなる。
 * 菱形と開始は図形の下に文を出さない（名前・入口の文は指すと出る `title`）ので、升目の高さに収まる。
 *
 * @param shape 形
 * @param heading 業務の節点に出す見出し（業務以外では使わない）
 * @returns 縦に占める高さ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の節点・札が縦に占める高さを、形と見出しの折り返しから見積もる。
 * @graph-connects estimateLineCount [calls] 折り返し後の行数
 */
export function footprintHeightOf(shape: DiagramShape, heading: string): number {
  if (shape === 'business') {
    const headingRoom =
      estimateLineCount(heading, NODE_TEXT_WIDTH, NODE_FONT_SIZE) * NODE_LINE_HEIGHT;
    return Math.max(NODE_HEIGHT, headingRoom + NODE_CHROME_HEIGHT);
  }
  return NODE_HEIGHT;
}

/** 開始・終了の枠線の色。 @graph-connects none */
const TERMINAL_BORDER = {
  start: 'border-[var(--color-success)]',
  end: 'border-[var(--color-text-muted)]',
} as const;

/** 節点の中身の共通 props。 */
export interface FlowNodeContentProps {
  /** 節点 */
  node: FlowNodeView;
  /** 表示名 */
  heading: string;
  /** 升目の幅 */
  cellWidth: number;
  /** 詳細ペインで開いているか */
  selected: boolean;
  /** この案件での差分の状態。差分が無ければ null */
  changeState: FlowChangeState | null;
  /** 開始の節点・菱形を指したときに出す文（入口の文・並列の説明）。無ければ出さない */
  hint?: string;
  /** 押したときに伝える。業務の節点だけが押せる */
  onSelect: () => void;
}

/**
 * 端の節点・菱形・札を、業務の節点と同じ高さの升目に収める枠。
 *
 * 図形を枠の**中央**に置く。縦中央に置くのは、線が出入りする高さを種別によらず升目の中心に
 * 揃えるためである。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の端の節点・菱形・札を、業務の節点と同じ高さの升目の中央に収める。
 * @graph-connects none
 */
function ControlSlot({ width, children }: { width: number; children: ReactNode }) {
  return (
    <div
      style={{ width, height: NODE_HEIGHT }}
      className="relative flex items-center justify-center"
    >
      {children}
    </div>
  );
}

/**
 * 業務の節点。業務名を折り返して全文を出す。
 *
 * 行数を決めて末尾を切ると、業務名の頭だけが並んだ図になり、どの業務かを図の上で見分けられない。
 * 高さは下限だけ決める。線が出入りする高さ（`NODE_HEIGHT / 2`）は下限の内側にあるので、伸びても
 * 線は箱に触れる。
 *
 * @param props 節点・選択・差分
 * @returns 業務の節点
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の業務の節点を、業務名・選択の枠・差分の地と印つきで描く。
 * @graph-connects FlowNodeChangeMark [calls] 変わった節点の上端の帯
 */
function BusinessNodeContent({
  node,
  heading,
  selected,
  changeState,
  onSelect,
}: FlowNodeContentProps) {
  return (
    <button
      type="button"
      data-story-node={node.nodeId}
      data-story-node-kind={node.kind}
      data-change-state={changeState ?? ''}
      aria-pressed={selected}
      onClick={onSelect}
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT, ...flowNodeFillStyle(changeState) }}
      className={[
        'relative flex flex-col justify-center gap-1 rounded bg-[var(--color-bg-surface)] px-3 py-2 text-left',
        selected
          ? 'border-2 border-[var(--color-accent-primary)]'
          : 'border border-[var(--color-border)] hover:border-[var(--color-border-hover)]',
        flowNodeBorderClass(changeState),
      ]
        .filter((part) => part !== '')
        .join(' ')}
    >
      <FlowNodeChangeMark state={changeState} />
      <span className="text-sm leading-5 font-medium break-words text-[var(--color-text-primary)]">
        {heading}
      </span>
    </button>
  );
}

/**
 * 開始・終了の節点（錠剤形）。開始はフローの入口の文を指すと出す（`title`）。
 *
 * 入口の文を錠剤の中にも下にも出さない（Dhik 2026-09-29「テキスト量が多いことが課題」）。錠剤は
 * 幅が決まっており、中に入れると「開始」の語が削られ、下に出すと図の上の文が増える。
 *
 * **押せない。** 開始・終了は中身を持たない（入口の文は `title` が持つ）ので、押しても右パネルを
 * 開かない。
 *
 * @param props 節点・差分・指すと出す文
 * @returns 開始・終了の節点
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の開始・終了の節点を押せない錠剤形で描き、開始は指すと入口の文を出す。
 * @graph-connects FlowNodeChangeMark [calls] 変わった節点の上端の帯
 */
function TerminalNodeContent({
  node,
  heading,
  cellWidth,
  changeState,
  hint,
}: FlowNodeContentProps) {
  const border = node.kind === 'start' ? TERMINAL_BORDER.start : TERMINAL_BORDER.end;
  return (
    <ControlSlot width={cellWidth}>
      <div
        data-story-node={node.nodeId}
        data-story-node-kind={node.kind}
        data-change-state={changeState ?? ''}
        {...(hint === undefined || hint === '' ? {} : { title: hint })}
        style={{
          width: TERMINAL_SHAPE_WIDTH,
          height: TERMINAL_SHAPE_HEIGHT,
          ...flowNodeFillStyle(changeState),
        }}
        className={[
          'relative flex items-center justify-center rounded-full border-2 bg-[var(--color-bg-surface)] px-3',
          border,
          flowNodeBorderClass(changeState),
        ]
          .filter((part) => part !== '')
          .join(' ')}
      >
        <FlowNodeChangeMark state={changeState} />
        <span className="truncate text-xs text-[var(--color-text-secondary)]">{heading}</span>
      </div>
    </ControlSlot>
  );
}

/** 菱形として回す正方形の一辺（px）。回した対角線が図形の幅（`PARALLEL_SHAPE_WIDTH`）に収まる。 @graph-connects none */
const DIAMOND_SIDE = 40;

/**
 * 並列の分岐・合流の節点（「＋」の菱形）。
 *
 * **正方形を回して**菱形にする。回すのは枠だけにして、「＋」は水平に置く。
 *
 * 種別の語（並列の分岐 ／ 並列の合流）と説明は図形の下に出さず、指すと出す（`title`）。形で
 * 分かるので、毎回読む文ではない。押しても右パネルを開かない（中身を全部 `title` が持つ）。
 *
 * 差分の印は付けない。並列の分岐・合流は業務ではなく、案件の差分の対象にならない。
 *
 * @param props 節点・種別の語・指すと出す説明
 * @returns 並列の分岐・合流の節点
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の並列の分岐・合流を押せない「＋」の菱形で描き、種別の語と説明を指すと出す。
 * @graph-connects none
 */
function ParallelNodeContent({ node, heading, cellWidth, hint }: FlowNodeContentProps) {
  return (
    <ControlSlot width={cellWidth}>
      <div
        role="img"
        aria-label={heading}
        data-story-node={node.nodeId}
        data-story-node-kind={node.kind}
        data-change-state=""
        title={hint === undefined || hint === '' ? heading : `${heading}\n${hint}`}
        style={{ width: PARALLEL_SHAPE_WIDTH, height: PARALLEL_SHAPE_WIDTH }}
        className="relative flex items-center justify-center"
      >
        <span
          aria-hidden="true"
          data-shape="diamond"
          style={{ width: DIAMOND_SIDE, height: DIAMOND_SIDE }}
          className="absolute rotate-45 border-2 border-[var(--color-text-primary)] bg-[var(--color-bg-surface)]"
        />
        <span
          aria-hidden="true"
          className="relative text-sm font-bold text-[var(--color-text-primary)]"
        >
          +
        </span>
      </div>
    </ControlSlot>
  );
}

/**
 * 節点 1 個の中身を形で選んで描く。
 *
 * @param props 節点・選択・差分・指すと出す文
 * @returns 節点の中身
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の節点を、種別（業務・開始・終了・並列の分岐と合流）ごとの形で描く。
 * @graph-connects BusinessNodeContent [calls] 業務の節点
 * @graph-connects TerminalNodeContent [calls] 開始・終了の節点
 * @graph-connects ParallelNodeContent [calls] 並列の分岐・合流の節点
 */
export function FlowNodeContent(props: FlowNodeContentProps) {
  if (props.node.kind === 'business') return <BusinessNodeContent {...props} />;
  if (props.node.kind === 'start' || props.node.kind === 'end') {
    return <TerminalNodeContent {...props} />;
  }
  return <ParallelNodeContent {...props} />;
}

/** 札の枠の高さ（px）。 @graph-connects none */
export const FLOW_TAG_HEIGHT = 56;

/**
 * 札（出口へ・行き先の無い止まる）。地を沈めた小さな枠で、選べない装飾にする。
 *
 * 札は業務フローの行き止まりで、終了の節点と同じ升目に置く。業務の節点とは、幅（錠剤形と同じ）と
 * 沈めた地で見分ける。**枠を破線にしない。** 破線は「案件で消えた」節点の印なので、札まで破線に
 * すると差分と関係ない札が消えたように見える。
 *
 * @param props 札の種類・升目の幅・中身
 * @returns 札
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の出口・止まるの札を、地を沈めた実線の枠で升目の中央に描く。
 * @graph-connects none
 */
export function FlowTagContent({
  kind,
  cellWidth,
  children,
}: {
  kind: 'exit' | 'stop';
  cellWidth: number;
  children: ReactNode;
}) {
  return (
    <ControlSlot width={cellWidth}>
      <div
        data-flow-tag={kind}
        style={{ width: TERMINAL_SHAPE_WIDTH, minHeight: FLOW_TAG_HEIGHT }}
        className="flex items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1 text-center text-[11px] leading-4 break-words text-[var(--color-text-secondary)]"
      >
        {children}
      </div>
    </ControlSlot>
  );
}
